import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Zap, Video, TrendingUp, PieChart as PieIcon, PlayCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const COLORS = ['#FF4B1F', '#FF9068', '#0088FE', '#00C49F', '#FFBB28'];

const styles = {
  container: { padding: '30px', color: 'var(--text-main)', height: '100%', overflowY: 'auto', maxWidth: '1200px', margin: '0 auto' },
  aiBanner: { background: 'var(--bg-card)', padding: '25px', borderRadius: '16px', marginBottom: '30px', border: '1px solid var(--border-color)', display: 'flex', gap: '20px', alignItems: 'flex-start' },
  metricsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' },
  metricCard: { background: 'var(--bg-card)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)' },
  metricLabel: { color: 'var(--text-muted)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' },
  metricValue: { fontSize: '2rem', fontWeight: 'bold' },
  
  // NEW GRID LAYOUT
  proGrid: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '30px', marginBottom: '30px' },
  card: { background: 'var(--bg-card)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border-color)', minHeight: '300px' },
  cardTitle: { margin: '0 0 20px 0', fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '10px' },

  // LATEST VIDEO STYLE
  latestVidContainer: { display: 'flex', flexDirection: 'column', gap: '15px' },
  statRow: { display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid var(--border-color)', paddingBottom: '10px' },
  statLabel: { color: 'var(--text-muted)' },
  statNum: { fontWeight: 'bold' }
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', padding: '10px', borderRadius: '8px' }}>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>{label}</p>
        <p style={{ margin: '5px 0', fontWeight: 'bold', color: 'var(--text-main)' }}>{data.views} Views</p>
        {data.upload && <div style={{ color: '#FF9068' }}>🚀 {data.upload}</div>}
      </div>
    );
  }
  return null;
};

const UploadDot = (props) => {
  const { cx, cy, payload } = props;
  if (payload.upload) return <circle cx={cx} cy={cy} r={6} stroke="#fff" strokeWidth={2} fill="#FF4B1F" style={{cursor: 'pointer'}} />;
  return null;
};

export default function Dashboard({ channel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!channel) return;
    setLoading(true);
    fetch(`${API_URL}/channels/${channel.id}/analytics`)
      .then(res => res.json())
      .then(json => { setData(json); setLoading(false); })
      .catch(err => { console.error(err); setLoading(false); });
  }, [channel]);

  const renderInsight = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      if (!line.trim()) return null;
      const parts = line.split('**');
      return (
        <p key={i} style={{margin: '0 0 8px 0', lineHeight: '1.6', color: '#ccc'}}>
          {parts.map((part, j) => j % 2 === 1 ? <strong key={j} style={{color: '#fff', fontWeight:'700'}}>{part}</strong> : part)}
        </p>
      );
    });
  };

  if (loading) return <div style={{textAlign:'center', padding:'50px', color:'var(--text-muted)'}}>Running Pro Analytics...</div>;
  if (!data || data.error) return <div style={{padding:'50px', color:'var(--text-main)'}}>⚠️ Error: {data?.error}</div>;

  const { summary, chart_data, traffic_sources, latest_video, ai_insight } = data;

  return (
    <div style={styles.container}>
      
      {/* AI BANNER */}
      <div style={styles.aiBanner}>
        <div style={{background: 'rgba(255, 75, 31, 0.2)', padding:'10px', borderRadius:'50%', color:'#FF4B1F', flexShrink:0}}><Zap size={24}/></div>
        <div style={{flex: 1}}>
          <h3 style={{margin: '0 0 10px 0', fontSize: '1.1rem'}}>Strategic Insight</h3>
          <div style={{color: 'var(--text-muted)'}}>{renderInsight(ai_insight)}</div>
        </div>
      </div>

      {/* KPI CARDS */}
      <div style={styles.metricsRow}>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Total Views (28d)</div>
          <div style={styles.metricValue}>{summary.views.toLocaleString()}</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Subscribers Gained</div>
          <div style={styles.metricValue}>+{summary.subs_gained}</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Newest Video Views</div>
          <div style={styles.metricValue}>{latest_video ? latest_video.views : '-'}</div>
        </div>
      </div>

      {/* MAIN LAYOUT */}
      <div style={styles.proGrid}>
        
        {/* LEFT: GRAPH */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}><TrendingUp size={20}/> Views & Uploads</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chart_data}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
              <XAxis dataKey="date" stroke="var(--text-muted)" fontSize={12} tickFormatter={d => d.slice(5)} />
              <YAxis stroke="var(--text-muted)" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Line type="monotone" dataKey="views" stroke="#FF4B1F" strokeWidth={3} dot={<UploadDot />} activeDot={{ r: 8 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* RIGHT: LATEST VIDEO STATS */}
        <div style={styles.card}>
          <h3 style={styles.cardTitle}><PlayCircle size={20}/> Latest Upload</h3>
          {latest_video ? (
            <div style={styles.latestVidContainer}>
              <div style={{fontSize:'0.9rem', marginBottom:'15px', fontWeight:'600'}}>{latest_video.title}</div>
              
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Views</span>
                <span style={styles.statNum}>{latest_video.views}</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Avg View Duration</span>
                <span style={styles.statNum}>{latest_video.avg_duration}s</span>
              </div>
              <div style={styles.statRow}>
                <span style={styles.statLabel}>Retention</span>
                <span style={{...styles.statNum, color: latest_video.avg_percent > 50 ? '#00cc66' : '#fff'}}>
                  {latest_video.avg_percent}%
                </span>
              </div>
              
              <div style={{marginTop: '20px', fontSize:'0.8rem', color:'#888', fontStyle:'italic'}}>
                 Tip: {latest_video.avg_percent < 40 ? "Retention is low. Try shorter intros." : "Great retention! Make more like this."}
              </div>
            </div>
          ) : (
             <div style={{color:'var(--text-muted)'}}>No recent videos found.</div>
          )}
        </div>
      </div>

      {/* BOTTOM: TRAFFIC SOURCES */}
      <div style={styles.proGrid}>
        <div style={styles.card}>
           <h3 style={styles.cardTitle}><PieIcon size={20}/> Traffic Sources</h3>
           <ResponsiveContainer width="100%" height={300}>
             <PieChart>
               <Pie data={traffic_sources} cx="50%" cy="50%" outerRadius={100} fill="#8884d8" dataKey="views" label={({name}) => name.replace(/_/g, ' ').toLowerCase()}>
                 {traffic_sources.map((entry, index) => (
                   <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                 ))}
               </Pie>
               <Tooltip />
             </PieChart>
           </ResponsiveContainer>
        </div>
        
        {/* Placeholder for future expansion */}
        <div style={{...styles.card, display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text-muted)'}}>
           Coming Soon: Audience Age/Gender
        </div>
      </div>

    </div>
  );
}